import {
  Autosuggest,
  Box,
  Button,
  Flashbar,
  FormField,
  Grid,
  Header,
  KeyValuePairs,
  Modal,
  SpaceBetween,
  StatusIndicator,
  TextFilter,
  Toggle,
} from '@amzn/awsui-components-react';
import { get } from 'lodash';
import React, { useCallback, useRef, useState } from 'react';
import { CommonDomainProps } from '../../../models/types';
import { CodeViewer } from './../../../Components/Common/CodeViewer';
import { DISCLOSURE_MESSAGE } from '../../../constants';
import {
  NODE_COMMANDS,
  NODE_SHELL_TOOL_PATH,
  NodeCommand,
} from './constants';
import { useExecuteCommandMutation } from './hooks/useExecuteCommandMutation';

/** Result of one command run, kept per command id so switching preserves output. */
interface CommandResult {
  /** Pretty-printed text form (for the code viewer / raw toggle). */
  raw: string;
  /** Parsed record object for structured rendering, when available. */
  record?: any;
  /** True when the run failed; raw holds the error message. */
  isError: boolean;
}

/**
 * Shell metacharacters that must not appear in free-text command input.
 * The backend already neutralizes these (validate_node_shell + shlex.quote on
 * aes/domain/dp/node/shell), so this is defense-in-depth plus a fail-fast UX:
 * we reject client-side rather than round-tripping to a backend rejection.
 */
const SHELL_METACHARACTERS = /[;&|`$<>(){}\n\r\\]/;

/**
 * Returns an error string if the free-text input for a command is invalid, or
 * null if it is acceptable. Only commands that take input are validated.
 */
export const validateInput = (
  command: NodeCommand,
  inputValue: string
): string | null => {
  if (!command.input) {
    return null;
  }
  const trimmed = inputValue.trim();
  if (command.input.required && trimmed === '') {
    return null; // emptiness is handled separately by the disabled state
  }
  if (SHELL_METACHARACTERS.test(trimmed)) {
    return 'Input contains disallowed shell characters (; & | ` $ < > ( ) { } \\).';
  }
  return null;
};

/** Builds the full args string, appending the user input for input-taking commands. */
export const buildArgs = (command: NodeCommand, inputValue: string): string => {
  const base = command.args ?? '';
  if (!command.input) {
    return base;
  }
  const trimmed = inputValue.trim();
  return trimmed ? `${base} ${trimmed}`.trim() : base;
};

/** Human-readable preview of what the command will run. */
export const commandPreview = (command: NodeCommand, inputValue: string): string => {
  if (command.toolPath && !command.baseCmd) {
    const argName = command.input?.argName;
    const value = inputValue.trim();
    return argName && value
      ? `${command.toolPath} (${argName}=${value})`
      : command.toolPath;
  }
  return `${command.baseCmd ?? ''} ${buildArgs(command, inputValue)}`.trim();
};

/** Turns a scalar value into a StatusIndicator for booleans / known statuses, else text. */
const renderValue = (value: any): React.ReactNode => {
  if (typeof value === 'boolean') {
    return (
      <StatusIndicator type={value ? 'success' : 'error'}>
        {value ? 'Yes' : 'No'}
      </StatusIndicator>
    );
  }
  if (value === 'Active') {
    return <StatusIndicator type="success">Active</StatusIndicator>;
  }
  if (value === 'Inactive') {
    return <StatusIndicator type="error">Inactive</StatusIndicator>;
  }
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'object') {
    return <code>{JSON.stringify(value)}</code>;
  }
  return String(value);
};

/**
 * Renders a record object as key/value pairs. When the record is keyed by
 * instance id (the is-os-containerized shape), each instance is a titled group
 * of its flattened fields; otherwise the object's own fields are shown.
 */
const StructuredResult = ({ record }: { record: any }) => {
  if (!record || typeof record !== 'object') {
    return <Box variant="p">No structured data.</Box>;
  }
  const entries = Object.entries(record);
  return (
    <SpaceBetween direction="vertical" size="l">
      {entries.map(([key, value]) => {
        // Flatten one level: a nested object becomes its own set of pairs.
        const inner =
          value && typeof value === 'object' ? (value as any) : { value };
        const flat: Record<string, any> = {};
        Object.entries(inner).forEach(([k, v]) => {
          if (v && typeof v === 'object') {
            Object.entries(v).forEach(([nk, nv]) => {
              flat[nk] = nv;
            });
          } else {
            flat[k] = v;
          }
        });
        return (
          <div key={key}>
            <Box variant="h4" padding={{ bottom: 'xxs' }}>
              {key}
            </Box>
            <KeyValuePairs
              columns={2}
              items={Object.entries(flat).map(([label, val]) => ({
                label,
                value: renderValue(val),
              }))}
            />
          </div>
        );
      })}
    </SpaceBetween>
  );
};

interface CommandRunnerProps extends CommonDomainProps {
  instanceId: string[];
  onClose: () => void;
}

const CommandRunner = (props: CommandRunnerProps) => {
  const [runCommand, { loading }] = useExecuteCommandMutation();

  const [selectedCommand, setSelectedCommand] = useState<NodeCommand>(
    NODE_COMMANDS[0]
  );
  const [inputValue, setInputValue] = useState(
    NODE_COMMANDS[0].input?.defaultValue ?? ''
  );
  // Output kept per command id so switching commands preserves each result.
  const [results, setResults] = useState<Record<string, CommandResult>>({});
  const [showRaw, setShowRaw] = useState(false);
  const [commandFilter, setCommandFilter] = useState('');
  // Synchronous in-flight guard. Apollo's `loading` only flips true after a
  // re-render, so two clicks in the same tick would both see loading===false
  // and both fire. A ref set synchronously blocks the second click.
  const inFlightRef = useRef(false);

  const result = results[selectedCommand.id];
  // Live validation error for the selected command's input (null when valid).
  const inputError = validateInput(selectedCommand, inputValue);

  // Case-insensitive match on label + description so the list stays usable as it grows.
  const filteredCommands = NODE_COMMANDS.filter((command) => {
    const needle = commandFilter.trim().toLowerCase();
    if (!needle) {
      return true;
    }
    return (
      command.label.toLowerCase().includes(needle) ||
      (command.description ?? '').toLowerCase().includes(needle)
    );
  });

  // Switch selection and reset the input field to the command's default.
  const handleSelect = useCallback((command: NodeCommand) => {
    setSelectedCommand(command);
    setInputValue(command.input?.defaultValue ?? '');
    setShowRaw(false);
  }, []);

  const setResult = useCallback((id: string, next: CommandResult) => {
    setResults((prev) => ({ ...prev, [id]: next }));
  }, []);

  const handleRun = useCallback(
    async (command: NodeCommand, currentInput: string) => {
      // Guard against re-entry: a rapid second click can fire before the
      // disabled state re-renders. The ref is set synchronously, so a second
      // click in the same tick is blocked (Apollo's `loading` would still be
      // stale-false here).
      if (inFlightRef.current) {
        return;
      }
      // Reject disallowed shell characters before dispatching (defense-in-depth;
      // the backend also validates). Surface the reason in the output pane.
      const inputError = validateInput(command, currentInput);
      if (inputError) {
        setResult(command.id, { raw: inputError, isError: true });
        return;
      }
      inFlightRef.current = true;
      try {
        // Dedicated-tool commands call their own tool path with just the
        // domain + instance; plain shell commands go through node/shell with
        // base_cmd + args.
        const isDedicatedTool = !!command.toolPath && !command.baseCmd;
        const variables = isDedicatedTool
          ? {
              url: command.toolPath as string,
              args: {
                domain_identifier: props.domainIdentifier,
                instance_id: props.instanceId[0],
                // Optional named argument for tools that take one (e.g. dmesg
                // `options`, openssl `port`). Only sent when the user provided a value.
                ...(command.input?.argName && currentInput.trim()
                  ? { [command.input.argName]: currentInput.trim() }
                  : {}),
              },
            }
          : {
              url: command.toolPath ?? NODE_SHELL_TOOL_PATH,
              args: {
                domain_identifier: props.domainIdentifier,
                base_cmd: command.baseCmd,
                args: buildArgs(command, currentInput),
                truncate_output: false,
                instance_id: props.instanceId[0],
              },
            };
        const resp = await runCommand({ variables });

        const executeCommand = get(resp, 'data.executeCommand', {} as any);
        if (!executeCommand.success) {
          setResult(command.id, {
            raw: `Error running command: ${get(
              executeCommand,
              'error.message',
              'Unknown error'
            )}`,
            isError: true,
          });
          return;
        }

        const data = executeCommand.data;
        if (data === null || data === undefined || data === '') {
          setResult(command.id, { raw: 'No output returned\n', isError: false });
          return;
        }
        if (typeof data === 'string') {
          const raw = data.endsWith('\n') ? data : data + '\n';
          setResult(command.id, { raw, isError: false });
          return;
        }
        // Structured envelope { record, table_id, headers } -- keep just the record.
        const record = 'record' in data ? data.record : data;
        setResult(command.id, {
          raw: JSON.stringify(record, null, 2) + '\n',
          record,
          isError: false,
        });
      } catch (e) {
        console.error('Error in handleRun:', e);
        // Surface the underlying error so operators can diagnose network /
        // GraphQL / timeout failures without opening the browser console.
        setResult(command.id, {
          raw: `Error running command: ${get(e, 'message', 'Unknown error')}`,
          isError: true,
        });
      } finally {
        inFlightRef.current = false;
      }
    },
    [props.instanceId, props.domainIdentifier, runCommand, setResult]
  );

  const renderResult = () => {
    if (!result) {
      return (
        <Box variant="p" color="text-body-secondary" padding={{ top: 's' }}>
          Run the command to see output here.
        </Box>
      );
    }
    // Structured commands render key/value pairs, with a raw-JSON escape hatch.
    if (
      selectedCommand.resultType === 'keyValue' &&
      !result.isError &&
      result.record
    ) {
      return (
        <SpaceBetween direction="vertical" size="s">
          <Toggle
            checked={showRaw}
            onChange={({ detail }) => setShowRaw(detail.checked)}
          >
            View raw JSON
          </Toggle>
          {showRaw ? (
            <CodeViewer
              codeData={result.raw}
              language={{ label: 'json', value: 'json' }}
              theme={{ label: 'coy', value: 'coy' }}
            />
          ) : (
            <StructuredResult record={result.record} />
          )}
        </SpaceBetween>
      );
    }
    return (
      <CodeViewer
        codeData={result.raw}
        language={{ label: 'neon', value: 'neon' }}
        theme={{ label: 'coy', value: 'coy' }}
      />
    );
  };

  return (
    <div>
      <Modal
        onDismiss={props.onClose}
        visible={true}
        closeAriaLabel="Close modal"
        size="max"
        header={
          <div>
            <Header variant="h2">
              {' '}
              Command Palette: <strong>{props.domainIdentifier}</strong>{' '}
            </Header>
            <Flashbar
              items={[
                {
                  type: 'warning',
                  content: DISCLOSURE_MESSAGE,
                },
              ]}
            />
          </div>
        }
      >
        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          {/* Left pane: supported commands, each with an inline run/play button */}
          <div
            style={{
              borderRight: '1px solid #e9ebed',
              paddingRight: '12px',
              minHeight: '400px',
            }}
          >
            <Box variant="h3" padding={{ bottom: 's' }}>
              Commands
            </Box>
            <Box padding={{ bottom: 's' }}>
              <TextFilter
                filteringText={commandFilter}
                filteringPlaceholder="Find command"
                filteringAriaLabel="Filter commands"
                onChange={({ detail }) =>
                  setCommandFilter(detail.filteringText)
                }
              />
            </Box>
            <SpaceBetween direction="vertical" size="xs">
              {filteredCommands.length === 0 ? (
                <Box variant="small" color="text-body-secondary">
                  No commands match "{commandFilter}"
                </Box>
              ) : null}
              {filteredCommands.map((command) => {
                const isSelected = selectedCommand.id === command.id;
                // Value the play button would run with: the live input for the
                // selected command, otherwise the command's default.
                const runValue = isSelected
                  ? inputValue
                  : command.input?.defaultValue ?? '';
                // Block starting another run while one is in flight; also honor
                // required-input validation.
                const runDisabled =
                  loading ||
                  (!!command.input?.required && runValue.trim() === '');
                return (
                  <div
                    key={command.id}
                    onClick={() => handleSelect(command)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      cursor: 'pointer',
                      padding: '8px 10px',
                      borderRadius: '8px',
                      backgroundColor: isSelected ? '#f0f7ff' : 'transparent',
                      border: isSelected
                        ? '1px solid #0972d3'
                        : '1px solid transparent',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{command.label}</div>
                      {command.description ? (
                        <Box variant="small" color="text-body-secondary">
                          {command.description}
                        </Box>
                      ) : null}
                    </div>
                    <Button
                      variant="icon"
                      iconName="caret-right-filled"
                      ariaLabel={`Run ${command.label}`}
                      loading={loading && isSelected}
                      disabled={runDisabled}
                      onClick={() => {
                        // Only re-select when switching commands. Re-selecting the
                        // already-selected command would reset inputValue to the
                        // default, so the field would no longer reflect the value
                        // actually being run.
                        if (selectedCommand.id !== command.id) {
                          handleSelect(command);
                        }
                        handleRun(command, runValue);
                      }}
                    />
                  </div>
                );
              })}
            </SpaceBetween>
          </div>

          {/* Right pane: optional input, command preview, output */}
          <SpaceBetween direction="vertical" size="m">
            {selectedCommand.input ? (
              <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
                <FormField
                  label={<strong>{selectedCommand.input.label}</strong>}
                  errorText={inputError ?? undefined}
                >
                  <Autosuggest
                    value={inputValue}
                    placeholder={selectedCommand.input.placeholder}
                    onChange={({ detail }) => setInputValue(detail.value)}
                    options={(selectedCommand.input.suggestions ?? []).map(
                      (path) => ({ value: path })
                    )}
                    enteredTextLabel={(value) => `Use: "${value}"`}
                    empty="No matching paths"
                    filteringType="auto"
                  />
                </FormField>
                <div style={{ marginTop: '26px' }}>
                  <Button
                    variant="primary"
                    iconName="caret-right-filled"
                    loading={loading}
                    disabled={
                      loading ||
                      !!inputError ||
                      (!!selectedCommand.input.required &&
                        inputValue.trim() === '')
                    }
                    onClick={() => handleRun(selectedCommand, inputValue)}
                  >
                    Run
                  </Button>
                </div>
              </Grid>
            ) : null}
            <Box variant="code" padding={{ top: 'xs' }}>
              {commandPreview(selectedCommand, inputValue)}
            </Box>
            {renderResult()}
          </SpaceBetween>
        </Grid>
      </Modal>
    </div>
  );
};

export { CommandRunner };
